import { useEffect, useRef, useState } from 'react';
import { cn } from '@rocco/utils/cn';
import { PublicationCovers } from '@modules-rocco/subscription/config/publication-covers';
import type { DigitalPublicationOption } from '@modules-rocco/subscription/api/get-digital-publication-options.server';
import { SubscribePublicationsModal } from '@modules-rocco/subscription/component/SubscribePublicationsModal';
import { button } from '~/ui-rocco/button-system';
import { usePrefersReducedMotion } from '~/ui-rocco/pages/detail-shared/use-prefers-reduced-motion';
import curatedCollectionCover from '../images/magazine/showcase-curated-collection.jpg';
import buildCover from '../images/magazine/showcase-build.jpg';
import commercialEditCover from '../images/magazine/showcase-commercial-edit.jpg';
import projectPortfolioCover from '../images/magazine/showcase-project-portfolio.jpg';

/** How many publications the showcase steps through. */
const SHOWN = 4;

/**
 * The showcase's own cover artwork per series (Figma rMiEd40Rr6anXK9ehTaRpe),
 * drawn large enough to fill the panel's half.
 */
const SHOWCASE_COVERS: Record<string, string> = {
    'Curated Collection': curatedCollectionCover,
    Build: buildCover,
    'Commercial Edit': commercialEditCover,
    'The Project Portfolio': projectPortfolioCover,
};

/** The showcase artwork, else the licensed series cover, else the issue's. */
const coverFor = (publication: DigitalPublicationOption) =>
    publication.isPlaceholder
        ? publication.coverImage
        : (SHOWCASE_COVERS[publication.newsletterType] ??
          PublicationCovers[publication.newsletterType] ??
          publication.coverImage);

const pad = (value: number) => String(value).padStart(2, '0');

/**
 * Where the reader is in the showcase: which publication is up. Read off the scroll position of the
 * tall track the panel is pinned inside, one viewport per publication.
 */
const useShowcaseStep = (count: number) => {
    const trackRef = useRef<HTMLDivElement>(null);
    const [index, setIndex] = useState(0);

    useEffect(() => {
        const track = trackRef.current;
        if (!track || count === 0) return;
        let frame = 0;
        const measure = () => {
            frame = 0;
            const rect = track.getBoundingClientRect();
            const travel = rect.height - window.innerHeight;
            const through =
                travel > 0 ? Math.min(Math.max(-rect.top / travel, 0), 1) : 0;
            // Same index, same state: React skips the render.
            setIndex(Math.min(Math.floor(through * count), count - 1));
        };
        const onScroll = () => {
            if (!frame) frame = requestAnimationFrame(measure);
        };
        measure();
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll);
        return () => {
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
            if (frame) cancelAnimationFrame(frame);
        };
    }, [count]);

    return { trackRef, index };
};

/**
 * The magazine section's publications, one at a time (desktop), after the
 * brand-book tone walk on brand.squarespace.com/color: a full-screen panel
 * pinned while the reader scrolls a viewport per publication. Each step
 * raises the publication's name and description from behind a mask, counts it
 * ("02 / 04") and brings its cover up on the right.
 */
export const PublicationsShowcase = ({
    publications,
    onSubscribed,
    className,
}: {
    publications: DigitalPublicationOption[];
    /** Called once the reader subscribes from the showcase's gate. */
    onSubscribed?: () => void;
    className?: string;
}) => {
    const shown = publications.slice(0, SHOWN);
    const { trackRef, index } = useShowcaseStep(shown.length);
    const reducedMotion = usePrefersReducedMotion();
    const [openPublication, setOpenPublication] =
        useState<DigitalPublicationOption | null>(null);
    const current = shown[index];
    if (!current) return null;

    return (
        <section
            aria-label="Digital publications"
            className={cn('relative w-full', className)}
        >
            {/* One viewport of scroll per publication; the panel stays put
                while the track passes behind it. */}
            <div
                ref={trackRef}
                style={{ height: `${shown.length * 100}vh` }}
                className="relative"
            >
                {/* On the section's own black, with its light grey type. */}
                <div className="sticky top-0 h-screen w-full overflow-hidden bg-[#141414] text-offwhite">
                    {/* The section header's columns: the text on the tags' column and
                        the gap after it, the cover on the paragraph's column,
                        so it sits centred under the copy above. */}
                    <div className="mx-auto grid h-full w-full max-w-(--ap-page-width) grid-cols-[57.96rem_52.752rem] px-[2.3124rem]">
                        {/* Left: the publication, rising in on each step, and
                            the count at the foot. */}
                        <div className="relative flex h-full flex-col justify-center pr-[4.8rem]">
                            <div key={current.hubspotID}>
                                <h3 className="m-0 overflow-hidden pb-[0.12em] font-sans text-[5.625rem] leading-[1.083] font-normal tracking-[-0.048em]">
                                    <span
                                        className={cn(
                                            'block',
                                            !reducedMotion && 'hero-line-rise',
                                        )}
                                    >
                                        {current.title}
                                    </span>
                                </h3>
                                <div className="mt-[1.5rem] max-w-[34rem] overflow-hidden">
                                    <p
                                        className={cn(
                                            'm-0 font-sans text-[1.5rem] leading-[1.3]',
                                            !reducedMotion && 'hero-line-rise',
                                        )}
                                        style={{ animationDelay: '120ms' }}
                                    >
                                        {current.description}
                                    </p>
                                </div>
                                <div className="mt-[2.5rem] overflow-hidden">
                                    <div
                                        className={cn(
                                            !reducedMotion && 'hero-line-rise',
                                        )}
                                        style={{ animationDelay: '240ms' }}
                                    >
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setOpenPublication(current)
                                            }
                                            className={button(
                                                'primary-inverse',
                                                'l',
                                            )}
                                        >
                                            Subscribe
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <p className="absolute bottom-[2.5rem] left-0 m-0 grid w-full grid-cols-[19.2rem_1fr] font-sans text-[1rem] tabular-nums">
                                <span>{pad(index + 1)}</span>
                                <span>/ {pad(shown.length)}</span>
                            </p>
                        </div>

                        {/* Right: the covers, stacked, the current one up,
                            in a frame of the covers' own proportion so the
                            masthead and strapline are never cropped, and no
                            wider than its column (71.56rem tall is the
                            52.752rem column's width). Each settles in from a
                            touch larger as it arrives. */}
                        <div className="flex h-full items-center justify-center">
                            <div className="relative aspect-[864/1172] h-[min(80vh,71.56rem)] shrink-0 overflow-hidden rounded-tile">
                                {shown.map((publication, position) => {
                                    const cover = coverFor(publication);
                                    const active = position === index;
                                    return cover ? (
                                        <img
                                            key={publication.hubspotID}
                                            src={cover}
                                            alt={
                                                active ? publication.title : ''
                                            }
                                            aria-hidden={!active}
                                            loading="lazy"
                                            decoding="async"
                                            className={cn(
                                                'absolute inset-0 size-full object-cover transition-[opacity,transform] duration-700 ease-out motion-reduce:transition-none',
                                                active
                                                    ? 'scale-100 opacity-100'
                                                    : 'scale-[1.04] opacity-0',
                                            )}
                                        />
                                    ) : null;
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* The site's own subscribe gate, arriving with the publication on
                screen ticked. */}
            <SubscribePublicationsModal
                open={openPublication !== null}
                initialNewsletterType={openPublication?.newsletterType}
                onClose={() => setOpenPublication(null)}
                onSubscribed={onSubscribed}
            />
        </section>
    );
};
